import { defaultOptions } from '@src/constants/options';
import type { IPlayerProxy } from '@src/controllers/PlayerWrapper';
import { injectErrorLogger } from '@src/hooks/beforeSetup';
import type { UserPlayerOptions } from '@src/types/player';

type Payload = Pick<
  UserPlayerOptions,
  | 'onInit'
  | 'onError'
  | 'onAdError'
  | 'onEvent'
  | 'trackingHandlers'
  | 'onPlay'
  | 'onPlaying'
  | 'onLoadStart'
  | 'onCanPlay'
  | 'onPause'
  | 'onEnded'
  | 'onVolumeChange'
  | 'onPlayerResize'
  | 'onResize'
  | 'onFullscreenChange'
>;

interface Options {
  playerProxyRef: React.MutableRefObject<IPlayerProxy | null | undefined>;
}

const keysOfOptionsToUpdateOnRender = [
  'onInit',
  'onError',
  'onAdError',
  'onEvent',
  'trackingHandlers',
  'onPlay',
  'onPlaying',
  'onLoadStart',
  'onCanPlay',
  'onPause',
  'onEnded',
  'onVolumeChange',
  'onPlayerResize',
  'onResize',
  'onFullscreenChange',
] as (keyof Payload)[];

const useSyncPlayerOptionsOnRender = (
  payload: Payload,
  hookOptions: Options
) => {
  const { playerProxyRef } = hookOptions;

  keysOfOptionsToUpdateOnRender.forEach((optionKey) => {
    if (!playerProxyRef.current?.options_) return;

    if (optionKey === 'onError') {
      playerProxyRef.current.options_.onError =
        payload?.onError ?? defaultOptions.onError;
      return;
    }

    if (optionKey === 'onAdError') {
      playerProxyRef.current.options_.onAdError =
        payload?.onAdError ?? defaultOptions.onAdError;
      return;
    }

    if (optionKey === 'onEvent') {
      playerProxyRef.current.options_.onEvent = payload?.onEvent;
      return;
    }

    if (optionKey === 'trackingHandlers') {
      playerProxyRef.current.options_.trackingHandlers =
        payload?.trackingHandlers;
      return;
    }

    if (playerProxyRef.current.options_[optionKey] !== payload?.[optionKey]) {
      playerProxyRef.current.options_[optionKey] = payload?.[optionKey];
    }
  });

  if (playerProxyRef.current?.options_) {
    injectErrorLogger(playerProxyRef.current.options_);
  }
};

export default useSyncPlayerOptionsOnRender;
